import React, { useCallback, useEffect, useRef, useState } from "react";

import {
  UserIcon,
  XCircleIcon,
  CalendarIcon,
  GlobeAltIcon,
  CheckCircleIcon,
  MagnifyingGlassIcon,
  ClipboardDocumentIcon,
} from "@heroicons/react/24/outline";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { Shimmer } from "../components/MotionPrimitives";
import Tooltip from "../components/Tooltip";
import { useAuth } from "../contexts/AuthContext";
import apiService from "../services/api";
import { buildDetailNavigationState } from "../utils/navigation";

const truncateText = (value, maxLength = 20) =>
  value.length > maxLength ? `${value.substring(0, maxLength)}...` : value;

const getUserAgentDisplayValue = (metadata = {}) =>
  metadata?.lunovaPlayerUserAgent || metadata?.userAgent || "";

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [showPageInput, setShowPageInput] = useState(false);
  const [pageInputValue, setPageInputValue] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [registrationFilter, setRegistrationFilter] = useState("all");
  const [domainFilter, setDomainFilter] = useState("all");
  const [domainOptions, setDomainOptions] = useState([]);
  const [userAgentFilter, setUserAgentFilter] = useState("all");
  const [userAgentOptions, setUserAgentOptions] = useState([]);
  const [loadingUserAgents, setLoadingUserAgents] = useState(false);
  const [deviceDistribution, setDeviceDistribution] = useState({
    categories: [],
    total: 0,
  });
  const [deviceDistributionError, setDeviceDistributionError] = useState(null);
  const [dateFromFilter, setDateFromFilter] = useState("");
  const [dateToFilter, setDateToFilter] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [deviceSortType, setDeviceSortType] = useState("count-desc"); // count-desc, count-asc, alpha-asc, alpha-desc
  const [pagination, setPagination] = useState({
    limit: 10,
    page: 1,
    pages: 0,
    total: 0,
  });
  const requestIdRef = useRef(0);
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const fetchUsers = useCallback(async () => {
    const requestId = requestIdRef.current + 1;

    requestIdRef.current = requestId;
    try {
      setLoading(true);
      setError(null);
      const params = {
        page: currentPage,
        limit: itemsPerPage,
      };

      if (debouncedSearchTerm) params.search = debouncedSearchTerm;
      if (statusFilter !== "all") params.status = statusFilter;
      if (registrationFilter !== "all")
        params.registration = registrationFilter;
      if (domainFilter !== "all") params.domain = domainFilter;
      if (userAgentFilter !== "all") params.userAgent = userAgentFilter;
      if (dateFromFilter) params.dateFrom = dateFromFilter;
      if (dateToFilter) params.dateTo = dateToFilter;
      if (sortField) {
        params.sort = sortField;
        params.order = sortDirection;
      }

      const response = await apiService.getUsers(params);

      if (requestId === requestIdRef.current) {
        setUsers(Array.isArray(response.data) ? response.data : []);
        setPagination({
          limit: Number(response.pagination?.limit) || itemsPerPage,
          page: Number(response.pagination?.page) || currentPage,
          pages: Number(response.pagination?.pages) || 0,
          total: Number(response.pagination?.total) || 0,
        });
      }
    } catch (err) {
      console.error("Error fetching users:", err);
      if (requestId === requestIdRef.current) {
        setUsers([]);
        setError("Failed to load users");
      }
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [
    currentPage,
    dateFromFilter,
    dateToFilter,
    debouncedSearchTerm,
    domainFilter,
    itemsPerPage,
    registrationFilter,
    sortDirection,
    sortField,
    statusFilter,
    userAgentFilter,
  ]);

  const fetchUserDomains = useCallback(async () => {
    try {
      const response = await apiService.getUserDomains();

      setDomainOptions(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error("Error fetching user domains:", err);
      toast.error("Failed to load domain filters");
    }
  }, []);

  const fetchUserAgentVersions = useCallback(async () => {
    try {
      setLoadingUserAgents(true);
      const response = await apiService.getUserAgentVersions({ limit: 500 });

      setUserAgentOptions(response.data || []);
    } catch (err) {
      console.error("Error fetching user agent versions:", err);
      toast.error("Failed to load user agent filters");
    } finally {
      setLoadingUserAgents(false);
    }
  }, []);

  const fetchDeviceDistribution = useCallback(async () => {
    try {
      setDeviceDistributionError(null);
      const params = {};

      if (userAgentFilter !== "all") params.userAgent = userAgentFilter;

      const response = await apiService.getDeviceDistribution(params);

      setDeviceDistribution({
        categories: Array.isArray(response.data?.categories)
          ? response.data.categories
          : [],
        total: Number(response.data?.total) || 0,
      });
    } catch (err) {
      console.error("Error fetching device distribution:", err);
      setDeviceDistribution({ categories: [], total: 0 });
      setDeviceDistributionError("Failed to load device distribution");
    }
  }, [userAgentFilter]);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
    }, 300);

    return () => window.clearTimeout(timeout);
  }, [searchTerm]);

  useEffect(() => {
    // Check if user is admin
    if (!user || user.role !== "admin") {
      navigate("/dashboard");

      return;
    }

    fetchUserAgentVersions();
    fetchUserDomains();
  }, [fetchUserAgentVersions, fetchUserDomains, navigate, user]);

  useEffect(() => {
    if (!user || user.role !== "admin") return;

    fetchUsers();
  }, [fetchUsers, user]);

  useEffect(() => {
    if (!user || user.role !== "admin") return;

    fetchDeviceDistribution();
  }, [fetchDeviceDistribution, user]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
    setCurrentPage(1); // Reset to first page when sorting
  };

  const uniqueDomains = domainOptions.map((option) => option.domain);

  const userAgentStats = deviceDistribution.categories;

  useEffect(() => {
    if (
      selectedCategory &&
      !userAgentStats.some((item) => item.category === selectedCategory)
    ) {
      setSelectedCategory(null);
    }
  }, [selectedCategory, userAgentStats]);

  // Get detailed device information for a selected category
  const getDeviceDetails = React.useMemo(() => {
    if (!selectedCategory) return [];

    const category = userAgentStats.find(
      (item) => item.category === selectedCategory
    );

    // Convert to array and sort based on sort type
    return [...(category?.devices || [])].sort((a, b) => {
      switch (deviceSortType) {
        case "count-desc":
          return b.count - a.count;
        case "count-asc":
          return a.count - b.count;
        case "alpha-asc":
          return a.device.localeCompare(b.device);
        case "alpha-desc":
          return b.device.localeCompare(a.device);
        default:
          return b.count - a.count;
      }
    });
  }, [userAgentStats, selectedCategory, deviceSortType]);

  // Color mapping for device categories - consistent colors per device type
  const getCategoryColor = (category) => {
    const categoryColors = {
      Web: "bg-blue-500",
      iOS: "bg-gray-800",
      Android: "bg-green-500",
      "Android TV": "bg-green-600",
      "Apple TV": "bg-gray-600",
      "Amazon Devices": "bg-orange-500",
      "Samsung TV": "bg-blue-600",
      "LG TV": "bg-red-500",
      Roku: "bg-purple-500",
      "TV / Set-Top Box": "bg-teal-500",
      Other: "bg-gray-400",
    };

    return categoryColors[category] || "bg-gray-400";
  };

  // Clear all filters
  const clearFilters = () => {
    setStatusFilter("all");
    setRegistrationFilter("all");
    setDomainFilter("all");
    setUserAgentFilter("all");
    setDateFromFilter("");
    setDateToFilter("");
    setCurrentPage(1);
  };

  // Check if any filters are active
  const hasActiveFilters =
    statusFilter !== "all" ||
    registrationFilter !== "all" ||
    domainFilter !== "all" ||
    userAgentFilter !== "all" ||
    dateFromFilter ||
    dateToFilter;

  const selectedUserAgentOptionMissing =
    userAgentFilter !== "all" &&
    !userAgentOptions.some((option) => option.userAgent === userAgentFilter);

  const paginatedUsers = users;
  const totalPages = pagination.pages;

  const formatDate = (date) => {
    if (!date) return "N/A";

    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (!user || user.role !== "admin") {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Access Denied
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            You don't have permission to view this page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Users
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Manage system users and their access
        </p>
      </div>

      {/* Search Bar and Items Per Page */}
      <div className="mb-6 flex items-center justify-between gap-4">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            className="w-full pl-10 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="Search users by first name, last name, username, domain, or email..."
            type="text"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
          {searchTerm && (
            <button
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              onClick={() => {
                setSearchTerm("");
                setCurrentPage(1);
              }}
            >
              <svg
                className="h-5 w-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  d="M6 18L18 6M6 6l12 12"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                />
              </svg>
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
            Show:
          </label>
          <select
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
      </div>

      {deviceDistributionError && (
        <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
          {deviceDistributionError}
        </div>
      )}

      {/* Device Distribution Statistics */}
      {userAgentStats.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 mb-6">
          <div className="flex items-center justify-between mb-3">
            <div className="flex flex-wrap items-center gap-2">
              <i className="fas fa-chart-pie text-gray-500 dark:text-gray-400" />
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                Device Distribution
              </h3>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                (Click to see details)
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {deviceDistribution.total.toLocaleString()} users with device
                data
              </span>
            </div>
            {selectedCategory && (
              <button
                className="text-xs px-3 py-1 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-md transition-colors font-medium"
                onClick={() => setSelectedCategory(null)}
              >
                <i className="fas fa-times mr-1" />
                Clear
              </button>
            )}
          </div>

          {/* Stacked Horizontal Bar Visualization */}
          <div className="flex h-10 rounded-lg overflow-hidden border-2 border-gray-300 dark:border-gray-600 shadow-sm">
            {userAgentStats.map((stat, index) => (
              <button
                className={`relative group transition-all ${getCategoryColor(stat.category)} hover:opacity-80`}
                key={index}
                style={{ width: `${stat.percentage}%` }}
                onClick={() =>
                  setSelectedCategory(
                    selectedCategory === stat.category ? null : stat.category
                  )
                }
              >
                {parseFloat(stat.percentage) > 10 && (
                  <span className="absolute inset-0 flex items-center justify-center text-xs font-semibold text-white">
                    {stat.percentage}%
                  </span>
                )}
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                  {stat.category}: {stat.count} users ({stat.percentage}%)
                </div>
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-3 mt-3">
            {userAgentStats.map((stat, index) => (
              <button
                className={`flex items-center gap-1.5 px-2 py-1 rounded-md transition-all ${
                  selectedCategory === stat.category
                    ? "bg-gray-200 dark:bg-gray-700"
                    : "hover:bg-gray-100 dark:hover:bg-gray-700/50"
                }`}
                key={index}
                onClick={() =>
                  setSelectedCategory(
                    selectedCategory === stat.category ? null : stat.category
                  )
                }
              >
                <div
                  className={`w-3 h-3 rounded ${getCategoryColor(stat.category)}`}
                />
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                  {stat.category}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  ({stat.percentage}%)
                </span>
              </button>
            ))}
          </div>

          {/* Device Details */}
          {selectedCategory && getDeviceDetails.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                  {selectedCategory} - Devices
                </h4>
                <div className="flex items-center gap-2">
                  <select
                    className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded border-none cursor-pointer focus:ring-1 focus:ring-primary-500"
                    value={deviceSortType}
                    onChange={(e) => setDeviceSortType(e.target.value)}
                  >
                    <option value="count-desc">Most Users</option>
                    <option value="count-asc">Least Users</option>
                    <option value="alpha-asc">A → Z</option>
                    <option value="alpha-desc">Z → A</option>
                  </select>
                  <button
                    className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                    onClick={() => setSelectedCategory(null)}
                  >
                    <i className="fas fa-times mr-1" />
                    Close
                  </button>
                </div>
              </div>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {getDeviceDetails.map((detail, idx) => (
                  <div
                    className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700/30 rounded"
                    key={idx}
                  >
                    <Tooltip position="top" text={detail.device}>
                      <button
                        className="flex-1 text-left text-xs text-gray-700 dark:text-gray-300 truncate hover:text-primary-600 dark:hover:text-primary-400 transition-colors flex items-center gap-1"
                        onClick={() => {
                          navigator.clipboard.writeText(detail.device);
                          toast.success("Device info copied!", {
                            position: "bottom-right",
                            autoClose: 2000,
                          });
                        }}
                      >
                        <span className="truncate">{detail.device}</span>
                        <ClipboardDocumentIcon className="h-3 w-3 flex-shrink-0 opacity-50" />
                      </button>
                    </Tooltip>
                    <span className="ml-3 text-xs font-medium text-gray-500 dark:text-gray-400">
                      {detail.count} {detail.count === 1 ? "user" : "users"}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Advanced Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md mb-6">
        <button
          className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
          onClick={() => setShowFilters(!showFilters)}
        >
          <div className="flex items-center gap-2">
            <i className="fas fa-filter text-gray-500 dark:text-gray-400" />
            <span className="font-medium text-gray-900 dark:text-white">
              Advanced Filters
            </span>
            {hasActiveFilters && (
              <span className="ml-2 px-2 py-0.5 text-xs bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300 rounded-full">
                Active
              </span>
            )}
          </div>
          <i
            className={`fas fa-chevron-${showFilters ? "up" : "down"} text-gray-400`}
          />
        </button>

        {showFilters && (
          <div className="px-6 pb-6 pt-2 border-t border-gray-200 dark:border-gray-700">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Status
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>

              {/* Registration Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Registration
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  value={registrationFilter}
                  onChange={(e) => {
                    setRegistrationFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="all">All Users</option>
                  <option value="has">Has Registration</option>
                  <option value="none">No Registration</option>
                </select>
              </div>

              {/* Domain Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Domain
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  value={domainFilter}
                  onChange={(e) => {
                    setDomainFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="all">All Domains</option>
                  {uniqueDomains.map((domain) => (
                    <option key={domain} value={domain}>
                      {domain}
                    </option>
                  ))}
                </select>
              </div>

              {/* App Version Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  App Version
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-60 disabled:cursor-not-allowed"
                  disabled={loadingUserAgents && userAgentOptions.length === 0}
                  value={userAgentFilter}
                  onChange={(e) => {
                    setUserAgentFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="all">
                    {loadingUserAgents
                      ? "Loading app versions..."
                      : "All App Versions"}
                  </option>
                  {selectedUserAgentOptionMissing && (
                    <option value={userAgentFilter}>{userAgentFilter}</option>
                  )}
                  {userAgentOptions.map((option) => (
                    <option key={option.userAgent} value={option.userAgent}>
                      {option.userAgent} ({option.count})
                    </option>
                  ))}
                </select>
              </div>

              {/* Date From Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Created From
                </label>
                <input
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  type="date"
                  value={dateFromFilter}
                  onChange={(e) => {
                    setDateFromFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </div>

              {/* Date To Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Created To
                </label>
                <input
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  type="date"
                  value={dateToFilter}
                  onChange={(e) => {
                    setDateToFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </div>

              {/* Clear Filters Button */}
              <div className="flex items-end">
                <button
                  className="w-full px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition-colors font-medium"
                  disabled={!hasActiveFilters}
                  onClick={clearFilters}
                >
                  <i className="fas fa-times mr-2" />
                  Clear Filters
                </button>
              </div>
            </div>

            {/* Filter Results Summary */}
            {hasActiveFilters && (
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex flex-wrap gap-2">
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Active filters:
                  </span>
                  {statusFilter !== "all" && (
                    <span className="inline-flex items-center px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded text-xs">
                      Status: {statusFilter}
                      <button
                        className="ml-1 hover:text-blue-900 dark:hover:text-blue-100"
                        onClick={() => setStatusFilter("all")}
                      >
                        <i className="fas fa-times text-xs" />
                      </button>
                    </span>
                  )}
                  {registrationFilter !== "all" && (
                    <span className="inline-flex items-center px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded text-xs">
                      Registration:{" "}
                      {registrationFilter === "has"
                        ? "Has Registration"
                        : "No Registration"}
                      <button
                        className="ml-1 hover:text-blue-900 dark:hover:text-blue-100"
                        onClick={() => setRegistrationFilter("all")}
                      >
                        <i className="fas fa-times text-xs" />
                      </button>
                    </span>
                  )}
                  {domainFilter !== "all" && (
                    <span className="inline-flex items-center px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded text-xs">
                      Domain: {domainFilter}
                      <button
                        className="ml-1 hover:text-blue-900 dark:hover:text-blue-100"
                        onClick={() => setDomainFilter("all")}
                      >
                        <i className="fas fa-times text-xs" />
                      </button>
                    </span>
                  )}
                  {userAgentFilter !== "all" && (
                    <span className="inline-flex items-center px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded text-xs">
                      App Version: {userAgentFilter}
                      <button
                        className="ml-1 hover:text-blue-900 dark:hover:text-blue-100"
                        onClick={() => setUserAgentFilter("all")}
                      >
                        <i className="fas fa-times text-xs" />
                      </button>
                    </span>
                  )}
                  {dateFromFilter && (
                    <span className="inline-flex items-center px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded text-xs">
                      From: {dateFromFilter}
                      <button
                        className="ml-1 hover:text-blue-900 dark:hover:text-blue-100"
                        onClick={() => setDateFromFilter("")}
                      >
                        <i className="fas fa-times text-xs" />
                      </button>
                    </span>
                  )}
                  {dateToFilter && (
                    <span className="inline-flex items-center px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded text-xs">
                      To: {dateToFilter}
                      <button
                        className="ml-1 hover:text-blue-900 dark:hover:text-blue-100"
                        onClick={() => setDateToFilter("")}
                      >
                        <i className="fas fa-times text-xs" />
                      </button>
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Users Table */}
      {loading ? (
        <div className="bg-white dark:bg-gray-800/50 rounded-xl shadow-md border border-gray-100 dark:border-gray-700/50 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  {Array.from({ length: 8 }).map((_, i) => (
                    <th className="px-6 py-3" key={i}>
                      <Shimmer className="h-3 w-20 rounded-md" />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                {Array.from({ length: 8 }).map((_, idx) => (
                  <tr key={idx}>
                    {Array.from({ length: 8 }).map((__, cellIdx) => (
                      <td className="px-6 py-4" key={cellIdx}>
                        <Shimmer className="h-3.5 w-full rounded-md" />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : error ? (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-600 dark:text-red-400">{error}</p>
        </div>
      ) : (
        <>
          <div className="bg-white dark:bg-gray-800/50 rounded-xl shadow-md border border-gray-100 dark:border-gray-700/50">
            <div
              className="overflow-x-auto scrollbar-visible"
              style={{
                WebkitOverflowScrolling: "touch",
                overflowX: "scroll",
              }}
            >
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 select-none min-w-[200px]"
                      onClick={() => handleSort("user")}
                    >
                      <div className="flex items-center gap-1">
                        User
                        {sortField === "user" && (
                          <i
                            className={`fas fa-chevron-${sortDirection === "asc" ? "up" : "down"} text-xs`}
                          />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 select-none min-w-[150px]"
                      onClick={() => handleSort("domain")}
                    >
                      <div className="flex items-center gap-1">
                        Domain
                        {sortField === "domain" && (
                          <i
                            className={`fas fa-chevron-${sortDirection === "asc" ? "up" : "down"} text-xs`}
                          />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 select-none min-w-[120px]"
                      onClick={() => handleSort("status")}
                    >
                      <div className="flex items-center gap-1">
                        Status
                        {sortField === "status" && (
                          <i
                            className={`fas fa-chevron-${sortDirection === "asc" ? "up" : "down"} text-xs`}
                          />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 select-none min-w-[150px]"
                      onClick={() => handleSort("registration")}
                    >
                      <div className="flex items-center gap-1">
                        Registration
                        {sortField === "registration" && (
                          <i
                            className={`fas fa-chevron-${sortDirection === "asc" ? "up" : "down"} text-xs`}
                          />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 select-none min-w-[180px]"
                      onClick={() => handleSort("created")}
                    >
                      <div className="flex items-center gap-1">
                        Created
                        {sortField === "created" && (
                          <i
                            className={`fas fa-chevron-${sortDirection === "asc" ? "up" : "down"} text-xs`}
                          />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 select-none min-w-[180px]"
                      onClick={() => handleSort("updated")}
                    >
                      <div className="flex items-center gap-1">
                        Last Updated
                        {sortField === "updated" && (
                          <i
                            className={`fas fa-chevron-${sortDirection === "asc" ? "up" : "down"} text-xs`}
                          />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 select-none min-w-[150px]"
                      onClick={() => handleSort("userAgent")}
                    >
                      <div className="flex items-center gap-1">
                        User Agent
                        {sortField === "userAgent" && (
                          <i
                            className={`fas fa-chevron-${sortDirection === "asc" ? "up" : "down"} text-xs`}
                          />
                        )}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {users.length === 0 ? (
                    <tr>
                      <td
                        className="px-6 py-12 text-center text-gray-500 dark:text-gray-400"
                        colSpan="7"
                      >
                        {searchTerm
                          ? "No users found matching your search"
                          : hasActiveFilters
                            ? "No users found matching your filters"
                            : "No users found"}
                      </td>
                    </tr>
                  ) : (
                    paginatedUsers.map((user) => (
                      <tr
                        className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        key={user.user_id}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0">
                              <UserIcon className="h-8 w-8 text-gray-400" />
                            </div>
                            <div className="ml-4 max-w-[180px]">
                              <div className="flex items-center gap-1 min-w-0">
                                <button
                                  className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 hover:underline transition-colors cursor-pointer text-left truncate"
                                  title={`View ${user.username}`}
                                  onClick={() =>
                                    navigate(`/users/${user.user_id}`, {
                                      state:
                                        buildDetailNavigationState(location),
                                    })
                                  }
                                >
                                  <span className="truncate">
                                    {user.username}
                                  </span>
                                </button>
                              </div>
                              {user.email_address && (
                                <div className="text-sm text-gray-500 dark:text-gray-400 truncate">
                                  {user.email_address}
                                </div>
                              )}
                              {user.matching_profile_names?.length > 0 && (
                                <div className="text-xs text-purple-600 dark:text-purple-400 truncate">
                                  Profile:{" "}
                                  {user.matching_profile_names.join(", ")}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center text-sm text-gray-900 dark:text-white max-w-[150px]">
                            <GlobeAltIcon className="h-4 w-4 mr-2 flex-shrink-0 text-gray-400" />
                            {user.domain ? (
                              <button
                                className="truncate hover:text-primary-600 dark:hover:text-primary-400 transition-colors cursor-pointer text-left flex items-center gap-1 min-w-0"
                                title={`Click to copy: ${user.domain}`}
                                onClick={() => {
                                  navigator.clipboard.writeText(user.domain);
                                  toast.success(`Copied: ${user.domain}`, {
                                    position: "bottom-right",
                                    autoClose: 2000,
                                  });
                                }}
                              >
                                <span className="truncate">{user.domain}</span>
                                <ClipboardDocumentIcon className="h-3.5 w-3.5 flex-shrink-0 opacity-50" />
                              </button>
                            ) : (
                              <span className="truncate">N/A</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              user.status === "active" ||
                              user.status === "ACTIVE"
                                ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300"
                                : user.status === "suspended"
                                  ? "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300"
                                  : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300"
                            }`}
                          >
                            {user.status === "active" ||
                            user.status === "ACTIVE" ? (
                              <CheckCircleIcon className="h-3 w-3 mr-1" />
                            ) : (
                              <XCircleIcon className="h-3 w-3 mr-1" />
                            )}
                            {user.status || "Active"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {user.registration_id ? (
                            <button
                              className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
                              onClick={() =>
                                navigate(`/customers/${user.registration_id}`, {
                                  state: buildDetailNavigationState(location),
                                })
                              }
                            >
                              View Registration
                            </button>
                          ) : (
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                              No registration
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                            <CalendarIcon className="h-4 w-4 mr-1" />
                            {formatDate(user.created_at)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {formatDate(user.updated_at)}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {user.metadata?.userAgent ? (
                            <Tooltip
                              position="top"
                              text={user.metadata.userAgent}
                            >
                              <button
                                className="text-xs text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors cursor-pointer text-left flex items-center gap-1 min-w-0 max-w-[150px]"
                                onClick={() => {
                                  navigator.clipboard.writeText(
                                    user.metadata.userAgent
                                  );
                                  toast.success("User agent copied!", {
                                    position: "bottom-right",
                                    autoClose: 2000,
                                  });
                                }}
                              >
                                <span className="truncate">
                                  {truncateText(
                                    getUserAgentDisplayValue(user.metadata)
                                  )}
                                </span>
                                <ClipboardDocumentIcon className="h-3.5 w-3.5 flex-shrink-0 opacity-50" />
                              </button>
                            </Tooltip>
                          ) : (
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              N/A
                            </div>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-6 flex items-center justify-between">
              <div className="text-sm text-gray-700 dark:text-gray-300">
                Showing{" "}
                <span className="font-medium">
                  {Math.min(
                    (currentPage - 1) * itemsPerPage + 1,
                    pagination.total
                  )}
                </span>{" "}
                to{" "}
                <span className="font-medium">
                  {Math.min(currentPage * itemsPerPage, pagination.total)}
                </span>{" "}
                of <span className="font-medium">{pagination.total}</span> users
              </div>
              <div className="flex gap-2 items-center">
                <button
                  className="px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                >
                  Previous
                </button>

                {/* Page Numbers */}
                {(() => {
                  const pages = [];
                  const maxVisible = 3;
                  let startPage = Math.max(
                    1,
                    currentPage - Math.floor(maxVisible / 2)
                  );
                  const endPage = Math.min(
                    totalPages,
                    startPage + maxVisible - 1
                  );

                  // Adjust start if we're near the end
                  if (endPage - startPage < maxVisible - 1) {
                    startPage = Math.max(1, endPage - maxVisible + 1);
                  }

                  // Show page range
                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(
                      <button
                        className={`px-3 py-2 text-sm font-medium border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 ${
                          currentPage === i
                            ? "bg-primary-600 text-white border-primary-600"
                            : "text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800"
                        }`}
                        key={i}
                        onClick={() => setCurrentPage(i)}
                      >
                        {i}
                      </button>
                    );
                  }

                  // Show ... with total pages if there are more pages
                  if (totalPages > maxVisible) {
                    pages.push(
                      <div
                        className="relative flex items-center gap-2"
                        key="ellipsis"
                      >
                        {showPageInput ? (
                          <input
                            autoFocus
                            className="w-16 px-2 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md text-center bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                            max={totalPages}
                            min={1}
                            placeholder="..."
                            type="number"
                            value={pageInputValue}
                            onBlur={() => {
                              setShowPageInput(false);
                              setPageInputValue("");
                            }}
                            onChange={(e) => setPageInputValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                const page = parseInt(pageInputValue, 10);

                                if (page >= 1 && page <= totalPages) {
                                  setCurrentPage(page);
                                }
                                setShowPageInput(false);
                                setPageInputValue("");
                              }
                            }}
                          />
                        ) : (
                          <button
                            className="px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                            onClick={() => setShowPageInput(true)}
                          >
                            ...
                          </button>
                        )}
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {totalPages}
                        </span>
                      </div>
                    );
                  }

                  return pages;
                })()}

                <button
                  className="px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={currentPage === totalPages}
                  onClick={() =>
                    setCurrentPage(Math.min(totalPages, currentPage + 1))
                  }
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Users;
